import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Truck } from "@phosphor-icons/react";

/**
 * DeliveryTimeSlider — POS main-page cushion for delivery ETAs.
 *
 * Visible only when first-party delivery is enabled AND the caller is an
 * admin/manager. Adjusts ``delivery_time_adjustment_min`` in
 * store_settings, which is added to every quote the online ordering
 * site computes. Useful during a Friday rush — tap it up to 20 min
 * while the kitchen catches up, tap it back down when things ease.
 *
 * Debounces PATCH calls so dragging doesn't spam the API.
 */
const DeliveryTimeSlider = ({ user }) => {
  const [enabled, setEnabled] = useState(false);
  const [value, setValue] = useState(0);
  const debouncer = useRef(null);
  const isManager = user?.role === "admin" || user?.role === "manager";

  useEffect(() => {
    if (!isManager) return;
    axios.get(`${API}/delivery/settings`).then((res) => {
      if (res.data?.first_party_delivery_enabled) {
        setEnabled(true);
        setValue(Number(res.data.delivery_time_adjustment_min || 0));
      }
    }).catch(() => {});
  }, [isManager]);

  const push = (v) => {
    if (debouncer.current) clearTimeout(debouncer.current);
    debouncer.current = setTimeout(() => {
      axios.put(`${API}/delivery/settings/adjustment`, {
        delivery_time_adjustment_min: v,
      }).catch(() => {});
    }, 300);
  };

  if (!isManager || !enabled) return null;

  return (
    <div
      className="flex items-center gap-2 pr-4 mr-2 border-r border-border"
      title="Add extra minutes to every delivery quote — great for slowing down orders during a rush"
      data-testid="delivery-time-slider"
    >
      <Truck size={16} weight="bold" className="text-primary shrink-0" />
      <div className="flex flex-col">
        <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>Delivery cushion</span>
          <span className="font-mono font-bold text-foreground" data-testid="delivery-cushion-value">
            +{value}m
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="60"
          step="1"
          value={value}
          onChange={(e) => {
            const v = Number(e.target.value);
            setValue(v);
            push(v);
          }}
          className="w-40 accent-primary"
          data-testid="delivery-cushion-input"
        />
      </div>
    </div>
  );
};

export default DeliveryTimeSlider;
